import { AddCartModalAction } from '../../actions/add_cart/modal_action';
import { AsCartItem } from '../../../item/models/large_commercial_pack_env';
import { TypedEvent } from '../../typed_event';
import { AnalyticsItem } from '../analytics/analytics_item';
import { AnalyticsItems } from '../analytics/analytics_items';

/**
 * 「複数商品を一括でカート追加できる機能」を担当するクラス。
 */
export class BulkAddCartService {
  static readonly BULK_ADD_CART_EVENT = new TypedEvent<AnalyticsItems>();

  /**
   * 一括登録用カートボタンがクリックされた時に実行される、イベントハンドラ。
   * @param event イベントオブジェクト
   */
  static async handleAddCartButtonClick(event: Event) {
    const bulkAddCartButton = event.currentTarget as HTMLButtonElement;

    // カート追加ボタンに対応する「数量エリアのまとまり」を特定するID
    const targetQuantitiesGroupId = bulkAddCartButton.getAttribute('data-target-quantities-group-id');

    // カート追加ボタンに対応する数量選択エリア要素を取得する。後続処理でmap()を使いたいので、Array.from()をしている。
    const quantityDivBlocks = Array.from(document.querySelectorAll(`.quantities-group-${targetQuantitiesGroupId}`));

    // エンドユーザーが数量を指定した商品の各種情報をHTMLから取得し、リストで保持する。
    const asCartRawItems =  quantityDivBlocks.map((quantityDivBlock) => this.createAsCartRawItem(quantityDivBlock))
                                          // nullを除去すればAsCartItem[]だと言い切れるので、キャストする。
                                          .filter(asCartRawItem => asCartRawItem !== null) as AsCartRawItem[];

    // カートに追加する対象がない場合は、何もしない。
    if (asCartRawItems.length === 0) return;

    // カート追加用の商品オブジェクトたちを構築する。
    const asCartItems = asCartRawItems.map((asCartRawItem) => this.createAsCartItem(asCartRawItem));

    // ツールチップidが指定された場合、画面のツールチップではなく、指定したツールチップを表示
    const addCartResultDisplayId = bulkAddCartButton.getAttribute('data-add-cart-result-display-id') || '';

    // 指定された全商品をカートに一括登録する。
    // 第二引数(id)は画面のツールチップ使用せず、特定のツールチップを表示したいときに設定する。
    // 第三引数(viewName)は最近チェックされた商品画面のみ指定すべきなので、ここでは指定不要。
    // addCartTogetherの終了を待って後続処理に進みたいので、awaitを付けている。
    await AddCartModalAction.addCartTogether(bulkAddCartButton, addCartResultDisplayId, '', asCartItems);

    // 数量の入力領域を、初期表示の状態である「プルダウンで『数量』が選択されている状態」に戻す。
    this.resetQuantityInputAreas(quantityDivBlocks);

    // カゴ追加が成功した商品の情報をAdobeに送る。
    this.bulkAddCartAnalytics(bulkAddCartButton, asCartRawItems);
  }

  /**
   * 「複数商品を一括でカート追加できる機能」のAdobe Analyticsトリガーを設定
   * @param targetElement カート追加ボタンのHTML要素
   * @param asCartRawItems 各種商品情報を持つオブジェクトのリスト
   */
  static async bulkAddCartAnalytics(targetElement: HTMLElement, asCartRawItems: AsCartRawItem[]) {
    const analyticsItems = asCartRawItems.map((asCartRawItem) => {
      const analyticsItem = new AnalyticsItem();
      analyticsItem._catalogItemCode = asCartRawItem.catalogItemCode;

      // Null合体演算子を使って、medicalMaterialTypeがnullの場合は空文字を設定する。
      analyticsItem._medicalMaterialType = asCartRawItem.medicalMaterialType ?? '';
      return analyticsItem;
    });
    // 一括登録された商品リスト情報と押下されたボタンの情報をAdobeに送る処理
    this.BULK_ADD_CART_EVENT.trigger({
      target: targetElement,
      analyticsData: {
        items: analyticsItems
      }
    });
  }

  /**
   * カート追加のパラメータの原型となるAsCartRawItemを生成する。
   * @param quantityDivBlock 単品ごとに設置される、数量入力用のdiv要素
   * @returns 入力された数量と商品キーが設定されたAsCartRawItem
   */
  private static createAsCartRawItem(quantityDivBlock: Element): AsCartRawItem | null {
    if (!(quantityDivBlock instanceof HTMLDivElement)) {
      // JSにはkotlinのmapNotNullのようなものが無いので、nullを返しておき後で除去する。
      return null;
    }

    // プルダウンで選択された数量
    const pulldownQuantity = Number(quantityDivBlock.querySelector('select')?.value);
    // テキストボックスに入力された数量
    const textQuantity = Number(quantityDivBlock.querySelector('input')?.value);

    // プルダウンで「10+」を選択すると、プルダウンのvalueは10になる。
    // なので、プルダウンのvalueが9以下であれば、プルダウンで数量が指定されている。
    // そうでない場合は、テキストボックスで数量が指定されている。
    const quantity = pulldownQuantity <= 9 ? pulldownQuantity : textQuantity;

    // プルダウンで「数量」が選択されているとNumber('')となり、ゼロとして扱われる。
    // テキストボックスで数字以外の文字列が入力されていると、NaNになる。
    // これらのいずれかに該当する場合は、有効な数量が指定されていないということなので、無視する。
    if (quantity === 0 || Number.isNaN(quantity)) {
      return null;
    }

    // 商品キーを取得する。
    const supplierId = quantityDivBlock.getAttribute('data-supplier-id');
    const catalogItemCode = quantityDivBlock.getAttribute('data-catalog-item-code');
    const medicalMaterialType = quantityDivBlock.getAttribute('data-medical-material-type');

    if (!quantity || !supplierId || !catalogItemCode) {
      return null;
    }

    return new AsCartRawItem(supplierId, catalogItemCode, quantity, medicalMaterialType);
  }

  private static createAsCartItem(asCartRawItem: AsCartRawItem): AsCartItem {
    return new AsCartItem(
      asCartRawItem.supplierId,
      asCartRawItem.catalogItemCode,
      asCartRawItem.quantity
    );
  }

  /**
   * 数量の入力領域を、初期表示の状態である「プルダウンで『数量』が選択されている状態」に戻す。
   * @param quantityDivBlocks 全単品の数量入力領域を表すDiv要素たち
   */
  private static resetQuantityInputAreas(quantityDivBlocks: Element[]) {
    quantityDivBlocks.forEach(quantityDivBlock => {
      const quantityPulldown = quantityDivBlock.querySelector('select');
      const quantityTextBox = quantityDivBlock.querySelector('input');

      if (quantityPulldown === null || quantityTextBox === null) return;

      // テキストボックスを空にしてフォーカスイン／アウトイベントを発生させることで、プルダウン表示に切り替える。
      // フォーカスイン時に画面の位置が移動しないようにオプションを追加
      quantityTextBox.value = '';
      quantityTextBox.focus({preventScroll:true});
      quantityTextBox.blur();

      // 「数量」表示に対応するvalueは空文字なので、空文字を設定することでプルダウン表示が「数量」になる。
      quantityPulldown.value = '';
    });
  }
}

/**
 * カート追加のパラメータの原型となるクラス。
 * AdobeAnalytics対応のためにmedicalMaterialTypeも合わせて取得している。
 */
class AsCartRawItem {
  supplierId: string;
  catalogItemCode: string;
  quantity: number;
  medicalMaterialType: string | null;

  constructor(
    supplierId: string,
    catalogItemCode: string,
    quantity: number,
    medicalMaterialType: string | null
  ) {
    this.supplierId = supplierId;
    this.catalogItemCode = catalogItemCode;
    this.quantity = quantity;
    this.medicalMaterialType = medicalMaterialType;
  }
}
